import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppreciationList from '../components/appreciation/AppreciationList';
import { useAppContext } from '../context/AppContext';
import { getSentAppreciations } from '../services/appreciationService';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAppContext();
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [isCheckingNewUser, setIsCheckingNewUser] = useState(true);

  // Check if this is a new user that just completed setup
  useEffect(() => {
    if (location.state?.setupComplete) {
      navigate('/welcome', { replace: true });
    }
  }, [location.state, navigate]);

  // Check if user is new (hasn't sent any appreciations)
  useEffect(() => {
    const checkNewUser = async () => {
      if (!currentUser) {
        setIsCheckingNewUser(false);
        return;
      }

      // Check if user has dismissed the banner
      const dismissed = localStorage.getItem('welcomeBannerDismissed');
      if (dismissed === 'true') {
        setIsCheckingNewUser(false);
        return;
      }

      try {
        const sentAppreciations = await getSentAppreciations(currentUser.username);
        if (sentAppreciations.length === 0) {
          setShowWelcomeBanner(true);
        }
      } catch (error) {
        console.error('Error checking new user status:', error);
      } finally {
        setIsCheckingNewUser(false);
      }
    };

    checkNewUser();
  }, [currentUser]);

  const handleDismissBanner = () => {
    localStorage.setItem('welcomeBannerDismissed', 'true');
    setShowWelcomeBanner(false);
  };

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 gradient-text">Welcome to Apprecio</h1>
        <p className="mt-2 text-sm text-gray-600">A platform for recognizing and appreciating your colleagues.</p>
      </div>

      {/* New User Welcome Banner */}
      {!isCheckingNewUser && showWelcomeBanner && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-l-4 border-indigo-500 p-6 rounded-xl shadow-md card-hover fade-in">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-3xl">🎉</span>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Welcome to Apprecio!
              </h3>
              <p className="mt-1 text-sm text-gray-700">
                Start building culture by sending your first appreciation. Recognize a colleague who demonstrates your company values!
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => navigate('/send-appreciation')}
                  className="btn-gradient text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm hover:shadow-md transition-all"
                >
                  Send Appreciation
                </button>
                <button
                  onClick={handleDismissBanner}
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
            <button
              onClick={handleDismissBanner}
              className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <AppreciationList />
    </div>
  );
};

export default Home;